import { DEFAULT_SETTINGS } from '@core/settings/schema.ts';
import { buildAutoApplyStylePatch } from '@core/styles/applyCustomStyleForLanguage.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import {
  LocalStorageSettingsStore,
} from '../adapters/settings/LocalStorageSettingsStore.ts';
import type { SubtitleStore, SubtitleSlot } from '../state/subtitleStore.ts';

/**
 * Extension `maybeApplyCustomStyleForLanguage`: runtime overlay only —
 * does not persist track style keys into saved settings.
 */
export function maybeApplyLanguageStyleRuntime(
  settingsStore: SettingsStore,
  slot: SubtitleSlot,
  languageCode: string | null | undefined,
): void {
  if (!languageCode || languageCode === 'null') return;
  const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
  const patch = buildAutoApplyStylePatch(
    languageCode,
    slot,
    settings.customStylePresets,
    settings.styleLanguagePriorities,
    settings,
  );
  if (!patch) return;
  if (settingsStore instanceof LocalStorageSettingsStore) {
    settingsStore.applyRuntimeOverlay(patch);
    return;
  }
  // Non-persistent stores only: never write language styles via set().
}

function selectedSubtitleLanguage(
  store: SubtitleStore,
  slot: SubtitleSlot,
): string | undefined {
  const state = store.getSlot(slot);
  const trackId = state.selectedId;
  if (!trackId) return undefined;
  return state.trackList?.subtitles.find((t) => String(t.value) === trackId)?.code;
}

/**
 * Extension `reapplyCustomStylesForSelectedSlots` —
 * subtitle languages first, then translation languages (translation wins per slot).
 */
export function reapplyCustomStylesForSelectedSlots(
  store: SubtitleStore,
  settingsStore: SettingsStore,
): void {
  const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
  const slots: Array<{
    slot: SubtitleSlot;
    subtitleCode: string | undefined;
    translationCode: string | undefined;
  }> = [
    {
      slot: 'main',
      subtitleCode: selectedSubtitleLanguage(store, 'main'),
      translationCode: settings.mainTranslations[0],
    },
    {
      slot: 'second',
      subtitleCode: selectedSubtitleLanguage(store, 'second'),
      translationCode: settings.secondTranslations[0],
    },
  ];

  for (const { slot, subtitleCode } of slots) {
    maybeApplyLanguageStyleRuntime(settingsStore, slot, subtitleCode);
  }
  for (const { slot, translationCode } of slots) {
    if (!translationCode || translationCode === 'null') continue;
    maybeApplyLanguageStyleRuntime(settingsStore, slot, translationCode);
  }
}
